import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import { refundReservation } from '../../api/endpoints/postSale';
import { voidShoppingCart } from '../../api/endpoints/shoppingCart';
import { REFUND_TYPES, type PostSaleResponse, type RefundType } from '../../api/types/postSale';
import type { AirPassengerBreakdown } from '../../api/types/air';
import type { ReservationReportRow } from '../../api/types/reports';
import type { ShoppingCartResponse } from '../../api/types/shoppingCart';
import { ApiErrorAlert } from '../ApiErrorAlert';

/** GUIDs + fare breakdown for one issued ticket, resolved from `GetShoppingCart` by
 *  matching the row's `documentNumber` against `passengerBreakdowns[].ticketNumber`. */
export interface TicketRef {
  bookingId?: string;
  paxId?: string;
  breakdown?: AirPassengerBreakdown;
}

const toNum = (s: string): number => {
  const n = Number.parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const ZERO_REFUND = {
  baseFare: '0',
  vq: '0',
  yr: '0',
  serviceFee: '0',
  extraServiceFee: '0',
  debit: '0',
  refundType: 2 as RefundType,
  description: '',
};

/**
 * Void / İade actions for a single issued ticket, shown on each row card of
 * `ReservationDetailPage` when the record is in the *ticket* state (see
 * reservation/capabilities.ts). The reservation state shows İptal Et instead — the two
 * are mutually exclusive.
 *
 * Both operations need the cart's `bookingId` + `paxId` GUIDs, which the flat `Entry/Find`
 * row lacks; the page resolves them from `GetShoppingCart` and passes them as `ticketRef`.
 * When they can't be matched the buttons are disabled with a hint. Business rules (void
 * window, refund ceilings) are enforced by the backend and surfaced via `ApiErrorAlert`.
 *
 * Refund form mirrors the legacy modal: `total = baseFare + vq + yr + serviceFee −
 * extraServiceFee` (auto, read-only); `total: 0` is a book-only refund.
 *
 * The "İade" here is the manual `ShoppingCart/Refund` accounting refund. The automated GDS
 * path (`Air/Modify/{Refund,Exchange}`) is NOT surfaced — that backend endpoint pair throws
 * `NotImplementedException` (docs/backend-requirements.md §F-12); the disabled placeholder
 * buttons were removed rather than shown greyed-out.
 */
export function TicketPostSaleActions({
  row,
  ticketRef,
  allowVoid,
  allowRefund,
  cartPending = false,
  onDone,
}: {
  row: ReservationReportRow;
  ticketRef: TicketRef;
  allowVoid: boolean;
  allowRefund: boolean;
  /** The cart lookup that resolves `ticketRef` is still in flight — suppress the
   *  "not matched" hint until it settles. */
  cartPending?: boolean;
  onDone: (res: PostSaleResponse | ShoppingCartResponse) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [dialog, setDialog] = useState<null | 'void' | 'refund'>(null);

  const [voidDesc, setVoidDesc] = useState('');
  const [refund, setRefund] = useState(ZERO_REFUND);

  const bd = ticketRef.breakdown;
  const canResolve = !!ticketRef.bookingId && !!ticketRef.paxId;
  const ticket = row.documentNumber || undefined;

  const openRefund = () => {
    setRefund(ZERO_REFUND);
    setDialog('refund');
  };

  const onError = (err: unknown) =>
    enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' });

  const voidMutation = useMutation({
    mutationFn: () =>
      voidShoppingCart(row.shoppingCartId ?? '', {
        bookingId: ticketRef.bookingId,
        paxId: ticketRef.paxId,
        pnr: row.pnr,
        ticket,
        description: voidDesc || undefined,
      }),
    onSuccess: (res) => {
      enqueueSnackbar(`${ticket ?? 'Bilet'} void edildi.`, { variant: 'success' });
      setDialog(null);
      setVoidDesc('');
      onDone(res);
    },
    onError,
  });

  const refundTotal =
    toNum(refund.baseFare) +
    toNum(refund.vq) +
    toNum(refund.yr) +
    toNum(refund.serviceFee) -
    toNum(refund.extraServiceFee);

  const refundMutation = useMutation({
    mutationFn: () =>
      refundReservation({
        shoppingCartId: row.shoppingCartId ?? '',
        bookingId: ticketRef.bookingId ?? '',
        paxId: ticketRef.paxId ?? '',
        pnr: row.pnr,
        ticket,
        description: refund.description || undefined,
        baseFare: toNum(refund.baseFare),
        vq: toNum(refund.vq),
        yr: toNum(refund.yr),
        debit: toNum(refund.debit),
        serviceFee: toNum(refund.serviceFee),
        extraServiceFee: toNum(refund.extraServiceFee),
        total: Number(refundTotal.toFixed(2)),
        refundType: refund.refundType,
      }),
    onSuccess: (res) => {
      enqueueSnackbar(res.successMessage ?? `${ticket ?? 'Bilet'} iade edildi.`, { variant: 'success' });
      setDialog(null);
      onDone(res);
    },
    onError,
  });

  if (!allowVoid && !allowRefund) return null;

  const pending = voidMutation.isPending || refundMutation.isPending;
  const refundField = (key: keyof typeof refund, label: string) => (
    <TextField
      size="small"
      label={label}
      value={refund[key] as string}
      onChange={(e) => setRefund((r) => ({ ...r, [key]: e.target.value }))}
      slotProps={{ htmlInput: { inputMode: 'decimal' } }}
    />
  );

  return (
    <>
      {allowVoid && (
        <Button
          size="small"
          color="warning"
          startIcon={<BlockOutlinedIcon />}
          disabled={!canResolve}
          onClick={() => {
            setVoidDesc('');
            setDialog('void');
          }}
        >
          Void
        </Button>
      )}
      {allowRefund && (
        <Button
          size="small"
          color="error"
          startIcon={<PaidOutlinedIcon />}
          disabled={!canResolve}
          onClick={openRefund}
        >
          İade
        </Button>
      )}
      {!canResolve && !cartPending && (
        <Typography variant="caption" color="text.secondary">
          Bilet numarası sepetle eşleştirilemedi
        </Typography>
      )}

      <Dialog open={dialog === 'void'} onClose={() => !pending && setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Bileti Void Et{ticket ? ` — ${ticket}` : ''}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Önce havayolu sisteminde void işlemini yapın. Bu işlem yalnızca bu sistemde ve Arkman üzerinde kayıt
              oluşturur; kredi kartı ödemelerini manuel iade edin.
            </Alert>
            <TextField
              label="Açıklama"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={voidDesc}
              onChange={(e) => setVoidDesc(e.target.value)}
            />
            <ApiErrorAlert error={voidMutation.error} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={pending}>
            Vazgeç
          </Button>
          <Button color="warning" variant="contained" disabled={pending} onClick={() => voidMutation.mutate()}>
            {voidMutation.isPending ? 'Void ediliyor…' : 'Void Et'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === 'refund'} onClose={() => !pending && setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Manuel İade{ticket ? ` — ${ticket}` : ''}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Önce havayolu sisteminde iade işlemini yapın. Bu işlem yalnızca bu sistemde ve Arkman üzerinde kayıt
              oluşturur; kredi kartı ödemelerini manuel iade edin.
            </Alert>
            <TextField
              select
              size="small"
              label="Yön bilgisi"
              value={refund.refundType}
              onChange={(e) => setRefund((r) => ({ ...r, refundType: Number(e.target.value) as RefundType }))}
            >
              {REFUND_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              {refundField('baseFare', 'Ana ücret')}
              {refundField('vq', 'Alan vergisi')}
              {refundField('yr', 'Yakıt vergisi')}
              {refundField('serviceFee', 'Servis bedeli')}
              {refundField('extraServiceFee', 'İade servis bedeli')}
              {refundField('debit', 'Ceza bedeli')}
            </Box>
            {bd && (
              <Typography variant="caption" color="text.secondary">
                Bilet üst sınırları — Ana ücret: {bd.baseFare ?? 0} · Vergi (alan+yakıt): {bd.totalTax ?? 0} · Servis
                bedeli: {bd.totalServiceFee ?? 0}. Boş bırakılırsa 0 gönderilir; toplam 0 ise kayıt bazlı iade yapılır.
              </Typography>
            )}
            <TextField
              size="small"
              label="Toplam iade"
              value={refundTotal.toFixed(2)}
              slotProps={{ input: { readOnly: true } }}
              helperText="Ana ücret + alan vergisi + yakıt vergisi + servis bedeli − iade servis bedeli"
            />
            <TextField
              label="Açıklama"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={refund.description}
              onChange={(e) => setRefund((r) => ({ ...r, description: e.target.value }))}
            />
            <ApiErrorAlert error={refundMutation.error} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={pending}>
            Vazgeç
          </Button>
          <Button color="error" variant="contained" disabled={pending} onClick={() => refundMutation.mutate()}>
            {refundMutation.isPending ? 'İade ediliyor…' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
